/**
 * /customer/notifications — inbox (Phase 2d).
 *
 * Mirror of mobile (customer)/notifications.tsx (lines 25–193):
 *   - INBOX / Notifications · {N} new header
 *   - Mark-all-read pill (visible only when unread > 0)
 *   - Optimistic per-row toggle-read with revert-on-failure
 *   - Empty: "You're all caught up" + mobile-verbatim body copy
 *
 * Operator decisions §1+§2 lock mobile parity: single fetch of 50, no
 * unread-only filter toggle, no Load More pager. `api.customerNotifications`
 * already hard-codes `?limit=50` so this page stays declarative.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ChevronLeft, Bell, Star, CheckCheck, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { timeAgo } from '@/lib/utils';

export default function CustomerNotificationsPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: qk.customerNotifications(),
    queryFn: () => api.customerNotifications(token),
    enabled: !!token,
    // The header bell badge polls the unread-count endpoint; the inbox
    // itself is fetched on mount + on every focus to keep parity with
    // mobile's `useFocusEffect` reload.
    refetchOnWindowFocus: true,
  });
  const items = listQ.data ?? [];
  const unread = items.filter((n) => !n.is_read).length;

  // Per-row mark-as-read — optimistic; on error we revert by surgical
  // patch instead of refetching, to feel as snappy as mobile.
  const markRead = useMutation({
    mutationFn: (nid) => api.customerMarkNotificationRead(token, nid),
    onMutate: async (nid) => {
      await qc.cancelQueries({ queryKey: qk.customerNotifications() });
      const prev = qc.getQueryData(qk.customerNotifications());
      qc.setQueryData(qk.customerNotifications(), (cur = []) =>
        cur.map((n) => (n.id === nid ? { ...n, is_read: true } : n)),
      );
      // Decrement badge optimistically.
      qc.setQueryData(qk.customerNotificationsUnread(), (cur) =>
        cur ? { ...cur, unread: Math.max(0, (cur.unread ?? 1) - 1) } : cur,
      );
      return { prev };
    },
    onError: (_e, _nid, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.customerNotifications(), ctx.prev);
      qc.invalidateQueries({ queryKey: qk.customerNotificationsUnread() });
      toast.error('Could not mark as read');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.customerNotificationsUnread() });
    },
  });

  const markAll = useMutation({
    mutationFn: () => api.customerMarkAllNotificationsRead(token),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: qk.customerNotifications() });
      const prev = qc.getQueryData(qk.customerNotifications());
      qc.setQueryData(qk.customerNotifications(), (cur = []) =>
        cur.map((n) => ({ ...n, is_read: true })),
      );
      qc.setQueryData(qk.customerNotificationsUnread(), { unread: 0 });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.customerNotifications(), ctx.prev);
      qc.invalidateQueries({ queryKey: qk.customerNotificationsUnread() });
      toast.error('Could not mark all as read');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.customerNotificationsUnread() });
    },
  });

  const onRowClick = useCallback(
    (n) => {
      if (!n.is_read) markRead.mutate(n.id);
    },
    [markRead],
  );

  return (
    <div className="space-y-6" data-testid="customer-notifications-screen">
      {/* Header row — mirrors mobile chevron-back + INBOX / Notifications */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="customer-notifications-back"
          className="w-9 h-9 rounded-full bg-[#15151B] border border-white/10 grid place-items-center text-white/75 hover:text-gold hover:border-gold/35 transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-gold">
            Inbox
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold leading-tight">
            Notifications
            {unread > 0 ? (
              <span className="text-gold text-base font-semibold ml-2">
                · {unread} new
              </span>
            ) : null}
          </h1>
        </div>
        {unread > 0 ? (
          <button
            type="button"
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending}
            data-testid="customer-notifications-mark-all-read"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-gold/12 text-gold text-xs font-semibold hover:bg-gold/18 transition-colors disabled:opacity-60"
          >
            {markAll.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <CheckCheck size={12} />
            )}
            Mark all read
          </button>
        ) : null}
      </div>

      {/* Body */}
      {listQ.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-20 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-3xl border border-white/8 bg-[#15151B] px-6 py-14 text-center"
          data-testid="customer-notifications-empty"
        >
          <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/25 grid place-items-center mx-auto mb-4">
            <Bell size={22} className="text-gold" />
          </div>
          <div className="font-display text-lg font-semibold">
            You&apos;re all caught up
          </div>
          <div className="text-sm text-white/55 mt-1 max-w-sm mx-auto">
            Stamp earned notifications and reward alerts will appear here.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <Row key={n.id} n={n} onClick={() => onRowClick(n)} />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ n, onClick }) {
  const isStamp = n.type === 'stamp_earned';
  const Icon = isStamp ? Star : Bell;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`customer-notif-row-${n.id}`}
      className={
        'w-full text-left flex items-start gap-3 px-4 py-3.5 rounded-2xl border transition-colors ' +
        (n.is_read
          ? 'bg-[#13131A] border-white/8 hover:border-white/12'
          : 'bg-gold/[0.06] border-gold/30 hover:bg-gold/[0.09]')
      }
    >
      <div
        className={
          'w-9 h-9 rounded-xl grid place-items-center shrink-0 border ' +
          (n.is_read
            ? 'bg-white/5 border-white/8 text-white/55'
            : 'bg-gold/15 border-gold/35 text-gold')
        }
      >
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={
            'text-sm font-semibold truncate ' +
            (n.is_read ? 'text-white/80' : 'text-white')
          }
        >
          {n.title}
        </div>
        {n.body ? (
          <div className="text-xs text-white/55 mt-0.5 line-clamp-2">
            {n.body}
          </div>
        ) : null}
        <div className="text-[11px] text-white/40 mt-1.5">
          {timeAgo(n.created_at)}
        </div>
      </div>
      {!n.is_read ? (
        <div
          className="w-2 h-2 rounded-full bg-gold mt-1.5 shrink-0"
          aria-hidden="true"
        />
      ) : null}
    </button>
  );
}
